import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { toast } from "sonner";
import {
  CheckCircle2,
  Circle,
  Clock,
  Package as PackageIcon,
  Store as StoreIcon,
  Loader2,
  ShieldCheck,
  Search,
  RefreshCw,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useTrackingWebSocket } from "@/hooks/useTrackingWebSocket";
import DeliveryRatingCard, { fireConfetti } from "@/components/DeliveryRatingCard";
import PushNotifyToggle from "@/components/PushNotifyToggle";

// Pretty status-flip label used in toasts + tab-title flicker.
const STATUS_NICE = {
  CREATED: "Package created",
  WAITING_FOR_PICKUP: "Waiting for pickup",
  PICKED_UP_BY_RIDER: "Picked up by rider",
  DROPPED_AT_WAREHOUSE: "Dropped at warehouse",
  RECEIVED_AT_WAREHOUSE: "Received at warehouse",
  SORTING_IN_WAREHOUSE: "Sorting in warehouse",
  ADDED_TO_OUTBOUND_BATCH: "Added to outbound batch",
  OUTBOUND_BATCH_READY: "Outbound batch ready",
  SHIPPED_OUT: "Shipped out from warehouse",
  READY_FOR_LAST_MILE_PICKUP: "Ready for last-mile pickup",
  OUT_FOR_LAST_MILE: "Out for last-mile delivery",
  DELIVERED: "Delivered to customer",
};

function StatusPill({ status, label }) {
  const tone = {
    DELIVERED: "bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/30",
    OUT_FOR_LAST_MILE:
      "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30",
    SHIPPED_OUT: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30",
    RECEIVED_AT_WAREHOUSE:
      "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30",
    PICKED_UP_BY_RIDER:
      "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    WAITING_FOR_PICKUP:
      "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700",
    PENDING:
      "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700",
  }[status] || "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  return (
    <span
      data-testid="track-status-pill"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-mono-tech uppercase tracking-wider ${tone}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      {label}
    </span>
  );
}

function Timeline({ steps, currentKey }) {
  return (
    <ol className="relative pl-6" data-testid="track-timeline">
      <span className="absolute left-[10px] top-1 bottom-1 w-px bg-slate-200 dark:bg-slate-800" />
      {steps.map((step) => {
        const isCurrent = step.key === currentKey;
        const Icon = step.reached ? CheckCircle2 : Circle;
        return (
          <li
            key={step.key}
            data-testid={`track-step-${step.key}`}
            className="relative pb-6 last:pb-0"
          >
            <span
              className={`absolute -left-6 top-0 w-5 h-5 grid place-items-center rounded-full ${
                step.reached
                  ? "text-cyan-600 dark:text-cyan-400 bg-white dark:bg-slate-950"
                  : "text-slate-400 dark:text-slate-600 bg-white dark:bg-slate-950"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <div
              className={`text-sm font-medium ${
                step.reached
                  ? "text-slate-900 dark:text-white"
                  : "text-slate-500"
              }`}
            >
              {step.label}
              {isCurrent && (
                <span className="ml-2 inline-block text-[10px] font-mono-tech uppercase tracking-wider text-cyan-600 dark:text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 px-1.5 py-0.5 rounded">
                  Current
                </span>
              )}
            </div>
            {step.at && (
              <div className="text-[11px] text-slate-500 font-mono-tech mt-0.5">
                {new Date(step.at).toLocaleString()}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ManualSearch() {
  const [code, setCode] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const v = code.trim();
        if (v) window.location.href = `/track/${encodeURIComponent(v)}`;
      }}
      className="mt-8 flex items-center gap-2 max-w-md mx-auto"
      data-testid="manual-track-form"
    >
      <div className="relative flex-1">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="manual-track-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter your tracking code (e.g. DBX-…)"
          className="pl-10"
        />
      </div>
      <Button
        type="submit"
        data-testid="manual-track-submit"
        className="bg-cyan-500 text-slate-950 hover:bg-cyan-400 dark:bg-cyan-400 dark:hover:bg-cyan-300 font-medium"
      >
        Track
      </Button>
    </form>
  );
}

export default function TrackPackage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);
  const lastStatusRef = useRef(null);
  const originalTitleRef = useRef(null);

  // Tab-title flicker for 6 seconds when a new status arrives in a background tab.
  const flickerTitle = (text) => {
    if (typeof document === "undefined") return;
    if (!originalTitleRef.current) originalTitleRef.current = document.title;
    if (!document.hidden) return; // only flicker if user isn't looking
    let on = true;
    const flicker = setInterval(() => {
      document.title = on ? `🔔 ${text}` : originalTitleRef.current;
      on = !on;
    }, 1100);
    setTimeout(() => {
      clearInterval(flicker);
      document.title = originalTitleRef.current;
    }, 6500);
  };

  const fetchOnce = async ({ silent = false } = {}) => {
    if (!code) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await api.get(`/public/track/${encodeURIComponent(code)}`);
      const prev = lastStatusRef.current;
      const next = r.data?.status;
      setData(r.data);
      setError("");
      setLastUpdated(new Date());
      if (prev && next && prev !== next) {
        const nice = STATUS_NICE[next] || next;
        toast.success(`Update: ${nice}`);
        flickerTitle(nice);
        // Celebrate a real-time DELIVERED flip with confetti.
        if (next === "DELIVERED" && prev !== "DELIVERED") {
          try { fireConfetti(); } catch (_) {/* noop */}
        }
      }
      lastStatusRef.current = next || lastStatusRef.current;
    } catch (e) {
      if (!silent) setError(formatApiError(e));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  // Live WebSocket — pushes status events immediately.
  const { status: wsStatus } = useTrackingWebSocket(code, () => {
    // Server signalled a change; refetch the full payload so timeline + items refresh.
    fetchOnce({ silent: true });
  });
  const live = wsStatus === "open";

  useEffect(() => {
    fetchOnce();
    // Polling fallback. When WS is connected we slow this way down (60s
    // safety net for missed events). When WS is closed/connecting we keep
    // the original 10s cadence so users on locked-down networks aren't stuck.
    const startPoll = () => {
      if (pollRef.current) return;
      const interval = wsStatus === "open" ? 60000 : 10000;
      pollRef.current = setInterval(() => fetchOnce({ silent: true }), interval);
    };
    const stopPoll = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    startPoll();
    const onVisibility = () => {
      if (document.hidden) stopPoll();
      else {
        fetchOnce({ silent: true });
        startPoll();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopPoll();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, wsStatus]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      <header className="sticky top-0 z-30 glass border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="track-brand-link">
            <Logo className="h-9" />
          </Link>
          <ThemeToggle size="sm" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400 mb-2">
          / Track Package
        </div>
        <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tighter mb-2">
          Where's my parcel?
        </h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm">
          Live status from store → warehouse → your door.
        </p>

        {loading && (
          <div
            data-testid="track-loading"
            className="mt-12 flex items-center justify-center gap-2 text-slate-500"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Loading tracking details…
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="track-error"
            className="mt-10 border border-red-300 dark:border-red-500/40 rounded-lg bg-red-50 dark:bg-red-500/10 p-6 text-center"
          >
            <div className="text-red-700 dark:text-red-300 font-display font-semibold mb-1">
              We couldn't find this tracking code.
            </div>
            <div className="text-xs text-red-600 dark:text-red-400">{error}</div>
            <ManualSearch />
          </div>
        )}

        {!loading && data && (
          <div className="mt-10 space-y-6" data-testid="track-result">
            {/* Live indicator */}
            <div className="flex items-center justify-between text-[11px] font-mono-tech text-slate-500">
              <span className="inline-flex items-center gap-2" data-testid="track-live-indicator">
                <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${live ? "bg-green-500" : "bg-amber-500"}`} />
                {live ? "Live · push connected" : "Live · auto-refreshing every 10s"}
              </span>
              <button
                onClick={() => fetchOnce({ silent: true })}
                data-testid="track-refresh-btn"
                className="inline-flex items-center gap-1.5 hover:text-slate-900 hover:dark:text-white transition-colors"
              >
                <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : "Refresh"}
              </button>
            </div>
            {/* Hero card */}
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-900/40">
              <div className="p-5 sm:p-7 flex flex-col sm:flex-row gap-5">
                <div className="w-full sm:w-40 h-40 shrink-0 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden grid place-items-center">
                  {data.product?.photo ? (
                    <img
                      src={data.product.photo}
                      alt={data.product?.name || "Product"}
                      data-testid="track-product-photo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <PackageIcon
                      className="w-12 h-12 text-slate-400 dark:text-slate-700"
                      strokeWidth={1.5}
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <StatusPill status={data.status} label={data.status_label} />
                    {data.package_id && (
                      <span
                        className="text-[11px] font-mono-tech text-slate-500"
                        data-testid="track-package-id"
                      >
                        {data.package_id}
                      </span>
                    )}
                    {data.store?.rating_avg != null && data.store?.rating_count > 0 && (
                      <span
                        data-testid="track-store-rating"
                        className="inline-flex items-center gap-1 text-[11px] font-mono-tech text-amber-600 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full"
                      >
                        ★ {Number(data.store.rating_avg).toFixed(1)} · {data.store.rating_count} review{data.store.rating_count === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  <h2
                    data-testid="track-product-name"
                    className="font-display text-2xl font-bold tracking-tight mb-1"
                  >
                    {data.product?.name || "Package"}
                  </h2>
                  {data.product?.description && (
                    <p
                      data-testid="track-product-description"
                      className="text-sm text-slate-600 dark:text-slate-400 mb-3"
                    >
                      {data.product.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <StoreIcon className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                    <span
                      className="text-slate-600 dark:text-slate-400"
                      data-testid="track-store-name"
                    >
                      Shipped by <strong className="text-slate-900 dark:text-white">{data.store?.name || "—"}</strong>
                    </span>
                  </div>
                </div>
              </div>

              {data.items && data.items.length > 0 && (
                <div className="border-t border-slate-200 dark:border-slate-800 px-5 sm:px-7 py-4">
                  <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-2">
                    / Items in this parcel
                  </div>
                  <ul className="space-y-1.5">
                    {data.items.map((it, i) => (
                      <li
                        key={i}
                        data-testid={`track-item-${i}`}
                        className="flex justify-between text-sm"
                      >
                        <span className="text-slate-700 dark:text-slate-300">{it.item_name}</span>
                        <span className="text-cyan-600 dark:text-cyan-300 font-mono-tech text-xs">
                          ×{it.quantity}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-5 sm:p-7 bg-slate-50 dark:bg-slate-900/40">
              <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> / Status Timeline
              </div>
              <Timeline steps={data.timeline || []} currentKey={data.status} />
            </div>

            {/* Rating card — appears when DELIVERED, replaces with thank-you state after rating */}
            {data.status === "DELIVERED" && !data.rating && (
              <DeliveryRatingCard
                code={code}
                data={data}
                onRated={(res) =>
                  setData((d) => ({
                    ...d,
                    rating: res.rating,
                    store: { ...(d.store || {}), ...(res.store || {}) },
                  }))
                }
              />
            )}
            {data.status === "DELIVERED" && data.rating && (
              <section
                data-testid="rating-thank-you"
                className="border border-cyan-500/40 rounded-xl p-5 sm:p-6 bg-gradient-to-br from-cyan-500/5 to-emerald-500/5"
              >
                <div className="flex items-center gap-3">
                  <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-300">
                    / You rated this delivery
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2 text-amber-500">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span key={n}>
                      {n <= data.rating.stars ? "★" : <span className="text-slate-400 dark:text-slate-700">★</span>}
                    </span>
                  ))}
                  <span className="ml-2 text-xs font-mono-tech text-slate-500">
                    {new Date(data.rating.rated_at).toLocaleDateString()}
                  </span>
                </div>
                {data.rating.comment && (
                  <p className="text-sm text-slate-700 dark:text-slate-300 italic mt-2">
                    "{data.rating.comment}"
                  </p>
                )}
                {data.rating.photo && (
                  <img
                    src={data.rating.photo}
                    alt="Your delivery"
                    className="mt-3 max-h-40 rounded-md border border-slate-200 dark:border-slate-800"
                  />
                )}
              </section>
            )}

            {/* Push notify toggle — hidden when unsupported or terminal status */}
            {data.status !== "DELIVERED" && data.status !== "CANCELLED" && (
              <PushNotifyToggle qrCodeId={code} />
            )}

            {/* Privacy notice + recipient CTA */}
            <div
              data-testid="track-privacy-notice"
              className="border border-slate-200 dark:border-slate-800 rounded-xl p-5 bg-slate-50 dark:bg-slate-900/40 flex items-start gap-3 text-sm"
            >
              <ShieldCheck className="w-5 h-5 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-display font-semibold text-slate-900 dark:text-white mb-0.5">
                  Are you the recipient?
                </div>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
                  Verify your identity with the last 4 digits of your phone to see the full
                  delivery record — name, address, signature, and proof photo.
                </p>
                <Link
                  to={`/recipient/${code}`}
                  data-testid="track-recipient-cta"
                  className="inline-flex items-center gap-1.5 text-xs font-mono-tech uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300 hover:text-cyan-600 dark:hover:text-cyan-200 border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 rounded-full"
                >
                  I'm the recipient →
                </Link>
              </div>
            </div>

            <div className="text-center pt-4">
              <Link
                to="/"
                data-testid="track-back-home"
                className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:dark:text-white transition-colors"
              >
                ← Back to DeliveryBoss
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
